/**
 * The pieces of one script field that both script layouts share (BV-2742): the schema
 * widget with its "Accept <extracted value>" chip, and the trailing evidence cluster.
 *
 * `ScriptStep`'s FieldRow lays them out beside a label; the multi-code grid
 * (`ScriptMatrixStep`) puts them in a cell under the code's column. Keeping them here
 * means a cell behaves exactly like a row — same commit, validation and Accept rules.
 */
import { FormFieldRenderer } from '@components/forms/FormFieldRenderer';
import { getLabel, shouldShowExtractedValue } from '@components/ui/editable-field';
import { HeardSeekButton } from '@components/ui/heard-seek-button';
import { ReasoningTooltip, ReviewButton } from '@components/ui/review-button';
import { Sparkles } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import type { FieldEvidence, HeardSeek } from '@/components/fields/types';
import { getValueAtPath } from '@/utils/auditCorrections';
import { getCallSourceId } from '@/utils/evidence';
import type { ResolvedField } from '@/utils/humanCallModel';
import { coercePhoneFieldValue } from '@/utils/phone';

/** Props for the field widget. */
export interface FieldControlProps {
  field: ResolvedField;
  readOnly: boolean;
  /** The extracted VALUES for this call — powers the per-field Accept affordance. */
  extractedData?: Record<string, unknown>;
  onFieldChange: (key: string, value: unknown) => void;
  onFieldCommit: (key?: string) => void;
  caseRecordId?: string;
  /** Validation messages keyed by field key (BV-2129). */
  fieldErrors?: Record<string, string>;
  /**
   * Grid-cell mode (BV-2742): the denser control height, and the widget's DOM id taken
   * from the field's full key. Every code's cell in a row shares one schema field id, so
   * the default would put the same `id` on each of them.
   */
  dense?: boolean;
}

/** The schema widget (disabled when read-only), its error, and the Accept chip. */
export const FieldControl: React.FC<FieldControlProps> = ({
  field,
  readOnly,
  extractedData,
  onFieldChange,
  onFieldCommit,
  caseRecordId,
  fieldErrors,
  dense = false,
}) => {
  // What PCE read off the call for this field. Offered as a one-click Accept rather than
  // written silently — the rep decides. `shouldShowExtractedValue` is the same rule the
  // Output board uses, so it hides once the field already holds that value (BV-1575).
  const extractedValue = getValueAtPath(extractedData, field.key);
  const showExtracted =
    !readOnly &&
    shouldShowExtractedValue({ extractedValue, value: field.value, field: field.field });

  return (
    <div
      className="min-w-0 flex-1"
      onKeyDown={e => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
    >
      {/* `error` renders below the widget even under `hideLabel` — FormFieldRenderer
          puts the message outside its label block — so the script gets the same
          feedback the accordion boards have always had. */}
      <FormFieldRenderer
        field={{ ...field.field, ...(dense && { id: field.key }), disabled: readOnly }}
        value={field.value}
        onChange={v => onFieldChange(field.key, v)}
        // Pass the key so the commit re-validates THIS field on blur — a typed
        // number whose save was refused mid-edit still has to report why (LA-7358).
        onBlur={() => onFieldCommit(field.key)}
        caseRecordId={caseRecordId}
        error={fieldErrors?.[field.key]}
        hideLabel
        compact={dense}
      />
      {showExtracted && (
        <button
          type="button"
          onClick={() => {
            // Extracted values do not come from the phone control, so they are not
            // in its dial-code encoding — writing one raw makes the control read the
            // leading digits as a country code (LA-7358). Re-encode it when it is a
            // real number, and refuse when it is not: filling the field with a value
            // the rep cannot save would also hide this chip, leaving no way to retry.
            const coerced = coercePhoneFieldValue(field.field, extractedValue);
            if (coerced.status === 'unusable') {
              toast.error(`Can't accept this value — ${coerced.message.toLowerCase()}.`);
              return;
            }
            onFieldChange(
              field.key,
              coerced.status === 'normalized' ? coerced.value : extractedValue,
            );
            onFieldCommit(field.key);
          }}
          className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
          title="Use the value the call extraction found"
        >
          <Sparkles className="h-3 w-3 shrink-0 text-amber-500" />
          <span className="truncate">Accept {getLabel(extractedValue, field.field)}</span>
        </button>
      )}
    </div>
  );
};

/** Props for the evidence cluster. */
export interface FieldEvidenceProps {
  field: ResolvedField;
  /**
   * Output metadata `fields` tree for the call, keyed by the same paths the script uses
   * (`drug[0].coverage.copay`). Present once extraction has run (BV-1575).
   */
  outputMetaFields?: Record<string, unknown>;
  /** The call's EXTRACTED metadata `fields` tree — the source that actually carries
   *  per-field evidence/reasoning. Same path keys as the data tree. */
  extractedMetaFields?: Record<string, unknown>;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
}

type LeafMeta = {
  evidence?: FieldEvidence[];
  heardSeek?: HeardSeek;
  source_id?: string;
  source_type?: 'call' | 'ai_audit';
};

/**
 * The Review / chain-of-thought / heard-seek trio the Output board puts on every field,
 * or null when the metadata carries none of them (i.e. before extraction has run).
 */
export function fieldEvidenceCluster({
  field,
  outputMetaFields,
  extractedMetaFields,
  onNavigateToCall,
  onNavigateToAudit,
}: FieldEvidenceProps): React.ReactNode | null {
  if (!onNavigateToCall) return null;
  // Per-field metadata leaf, resolved by the SAME path the value uses — the metadata tree
  // mirrors the data tree, so `drug[0].coverage.copay` indexes both (BV-1575).
  const outMeta = getValueAtPath(outputMetaFields, field.key) as LeafMeta | undefined;
  const extMeta = getValueAtPath(extractedMetaFields, field.key) as LeafMeta | undefined;
  // Evidence comes from whichever tree carries it. `output_data.__metadata__` describes the
  // PERSISTED value and is the better source in principle, but in practice it is essentially
  // never written — zero calls on dev/QA have it — while `extracted_data.output.metadata`
  // holds the real per-field evidence. Prefer output when present so a value edited after
  // extraction shows its own provenance; fall back to extracted otherwise (BV-1575).
  const evidenceMeta = outMeta?.evidence?.length ? outMeta : extMeta;
  const evidence = evidenceMeta?.evidence;
  // Heard-seek only ever rides on the output tree — mergeHeardSeek writes it there.
  const heardSeek = outMeta?.heardSeek;
  if (!evidence?.length && !heardSeek) return null;
  return (
    <>
      {!!evidence?.length && (
        <>
          <ReviewButton
            sourceCallId={getCallSourceId(evidenceMeta)}
            evidence={evidence}
            onNavigateToCall={onNavigateToCall}
            onNavigateToAudit={onNavigateToAudit}
            className="text-xs"
          />
          <ReasoningTooltip evidence={evidence} />
        </>
      )}
      {heardSeek && <HeardSeekButton heardSeek={heardSeek} onNavigateToCall={onNavigateToCall} />}
    </>
  );
}
